/**
 * 产品首页内容管理 - API 服务层
 *
 * 基于 VITE_USE_MOCK 控制：VITE_USE_MOCK=true 时使用 Mock；
 * 默认调用真实后端 API。
 */

import type {
  PageSection, HeroContent, FeatureCard, DataStat, Testimonial,
  DocEntry, NewsEntry, ProcessStep, BoardColumn, WorkScenario,
  CtaContent, FooterLinkGroup, SeoMetadata, LandingPageData,
} from '@/types/landingPage'
import {
  getSections as mockGetSections, updateSections as mockUpdateSections,
  getPublishedData, getDraftData, ensureDraft,
  saveDraftHero, saveDraftFeatures, saveDraftStats,
  saveDraftTestimonials, saveDraftDocEntries, saveDraftNewsEntries,
  saveDraftProcessSteps, saveDraftScenarios, saveDraftBoardColumns,
  saveDraftCta, saveDraftFooter, saveDraftSeo,
  publishDraft,
} from '@/mock/landingPage'

/** 是否使用后端 API（基于 VITE_USE_MOCK：不启用 Mock 时调用后端，默认走真实 API） */
const USE_BACKEND_API = import.meta.env.VITE_USE_MOCK !== 'true'

/** 首页内容管理服务接口（所有方法改为异步） */
export interface ILandingPageService {
  // 区块配置
  getSections(): Promise<PageSection[]>
  /** 公开可访问 — 仅返回已发布的区块信息 */
  getPublicSections(): Promise<PageSection[]>
  updateSections(sections: PageSection[]): Promise<void>
  updateSectionStatus(key: string, status: PageSection['status']): Promise<void>

  // 数据读取
  getPublished(): Promise<LandingPageData>
  getDraft(): Promise<LandingPageData>
  getPreview(): Promise<LandingPageData>

  // Hero
  saveHero(content: HeroContent): Promise<void>

  // Features
  saveFeatures(features: FeatureCard[], sortOrder: string[]): Promise<void>

  // Stats
  saveStats(stats: DataStat[], primaryStat: DataStat): Promise<void>

  // Testimonials
  saveTestimonials(testimonials: Testimonial[], sortOrder: string[]): Promise<void>

  // Docs / News
  saveDocEntries(entries: DocEntry[], sortOrder: string[]): Promise<void>
  saveNewsEntries(entries: NewsEntry[], sortOrder: string[]): Promise<void>

  // Process Steps
  saveProcessSteps(steps: ProcessStep[]): Promise<void>

  // Scenarios
  saveScenarios(scenarios: WorkScenario[], sortOrder: string[]): Promise<void>

  // Kanban
  saveBoardColumns(columns: BoardColumn[]): Promise<void>

  // CTA / Footer
  saveCta(content: CtaContent): Promise<void>
  saveFooter(groups: FooterLinkGroup[]): Promise<void>

  // SEO
  saveSeo(seo: SeoMetadata): Promise<void>

  // Publish（按区块 key 发布；key 与后端 page_sections.section_key 一致，如 'hero'/'process'/'board'/'docs'/'news'）
  publishSections(keys: string[]): Promise<void>

  // Publish
  publish(): Promise<LandingPageData>
}

/** Mock 实现 —— 将同步 mock 调用包装为 Promise */
const mockService: ILandingPageService = {
  async getSections() { return mockGetSections() },
  // 与真实 API 口径一致:仅返回已发布(未隐藏)区块,否则 mock 模式下"隐藏区块"对公开页无效
  async getPublicSections() {
    return (await mockGetSections()).filter(s => s.status !== 'hidden')
  },
  async updateSections(sections) { mockUpdateSections(sections) },
  async updateSectionStatus(_key: string, _status: PageSection['status']) { /* mock 模式下 updateSections 已持久化 */ },

  async getPublished() { return getPublishedData() },
  async getDraft() {
    const d = getDraftData()
    return d || getPublishedData()
  },
  async getPreview() { return ensureDraft() },

  async saveHero(content) { saveDraftHero(content) },
  async saveFeatures(features, sortOrder) { saveDraftFeatures(features, sortOrder) },
  async saveStats(stats, primaryStat) { saveDraftStats(stats, primaryStat) },
  async saveTestimonials(testimonials, sortOrder) { saveDraftTestimonials(testimonials, sortOrder) },
  async saveDocEntries(entries, sortOrder) { saveDraftDocEntries(entries, sortOrder) },
  async saveNewsEntries(entries, sortOrder) { saveDraftNewsEntries(entries, sortOrder) },
  async saveProcessSteps(steps) { saveDraftProcessSteps(steps) },
  async saveScenarios(scenarios, sortOrder) { saveDraftScenarios(scenarios, sortOrder) },
  async saveBoardColumns(columns) { saveDraftBoardColumns(columns) },
  async saveCta(content) { saveDraftCta(content) },
  async saveFooter(groups) { saveDraftFooter(groups) },
  async saveSeo(seo) { saveDraftSeo(seo) },

  async publishSections(_keys: string[]) { await publishDraft() },
  async publish() { return publishDraft() },
}

// Lazy-load API service
let apiService: ILandingPageService | null = null

async function getApiService(): Promise<ILandingPageService> {
  if (!apiService) {
    const mod = await import('@/services/landingPageApi')
    const service: ILandingPageService = {
      async getSections() { return mod.landingPageApiService.getSections() },
      async getPublicSections() { return mod.landingPageApiService.getPublicSections() },
      async updateSections(sections) { return mod.landingPageApiService.updateSections(sections) },
      async updateSectionStatus(key: string, status: PageSection['status']) { return (mod.landingPageApiService as any).updateSectionStatus(key, status) },
      async getPublished() { return mod.landingPageApiService.getPublished() },
      async getDraft() { return mod.landingPageApiService.getDraft() },
      async getPreview() { return mod.landingPageApiService.getPreview() },
      async saveHero(content) { return mod.landingPageApiService.saveHero(content) },
      async saveFeatures(f, so) { return mod.landingPageApiService.saveFeatures(f, so) },
      async saveStats(s, ps) { return mod.landingPageApiService.saveStats(s, ps) },
      async saveTestimonials(t, so) { return mod.landingPageApiService.saveTestimonials(t, so) },
      async saveDocEntries(e, so) { return mod.landingPageApiService.saveDocEntries(e, so) },
      async saveNewsEntries(e, so) { return mod.landingPageApiService.saveNewsEntries(e, so) },
      async saveProcessSteps(s) { return mod.landingPageApiService.saveProcessSteps(s) },
      async saveScenarios(s, so) { return mod.landingPageApiService.saveScenarios(s, so) },
      async saveBoardColumns(c) { return mod.landingPageApiService.saveBoardColumns(c) },
      async saveCta(c) { return mod.landingPageApiService.saveCta(c) },
      async saveFooter(g) { return mod.landingPageApiService.saveFooter(g) },
      async saveSeo(s) { return mod.landingPageApiService.saveSeo(s) },
      async publishSections(keys: string[]) { return mod.landingPageApiService.publishSections(keys) },
      async publish() { return mod.landingPageApiService.publish() },
    }
    apiService = service
  }
  return apiService
}

/** 导出 landingPageService —— 当前为 mock 实现（供 store 使用） */
export const landingPageService: ILandingPageService = USE_BACKEND_API
  ? new Proxy({} as ILandingPageService, {
      get(_, prop: string) {
        return (...args: unknown[]) => getApiService().then(s => (s as any)[prop](...args))
      },
    })
  : mockService

export function pushAuditLog(
  sectionKey: string,
  sectionName: string,
  action: string,
  summary: string,
  beforeSnapshot?: Record<string, unknown> | null,
  afterSnapshot?: Record<string, unknown> | null,
): void {
  // 后端模式下留痕由服务端切面写入，前端不再补记
  if (USE_BACKEND_API) return
  // mock 审计种子按需加载：真实后端模式不会走到这里，也就不该把 mock/audit-log.ts 打进包
  void import('@/mock/audit-log').then(({ recordMockAudit }) => {
    recordMockAudit({
      module: 'content',
      action,
      targetType: 'section',
      targetId: sectionKey,
      targetName: sectionName,
      summary,
      beforePayload: beforeSnapshot ?? null,
      afterPayload: afterSnapshot ?? null,
    })
  })
}

/** 上传用户证言头像,返回可访问 URL(mock 模式落 dataURL) */
export async function uploadTestimonialAvatar(file: File): Promise<string> {
  if (USE_BACKEND_API) {
    const mod = await import('@/services/landingPageApi')
    return mod.uploadTestimonialAvatar(file)
  }
  return readAsDataUrl(file)
}

/** 上传资讯封面图,返回可访问 URL(mock 模式落 dataURL) */
export async function uploadNewsCover(file: File): Promise<string> {
  if (USE_BACKEND_API) {
    const mod = await import('@/services/landingPageApi')
    return mod.uploadNewsCover(file)
  }
  return readAsDataUrl(file)
}

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('读取文件失败'))
    reader.readAsDataURL(file)
  })
}
